/**
 * The palette: every colour ROLE the app paints with, in light and in dark.
 *
 * Plan: docs/plans/DARK_MODE_SPRINT.md. Decisions D1–D17 are on the board linked
 * from that plan.
 *
 * ── How to read this file ────────────────────────────────────────────────────
 *
 * A ROLE is a job, not a colour: `ink.accent` is "green words", `fill.brand` is "a
 * green fill". In light they are both Cedar Prime. In dark they part ways — green
 * words become thrive (Cedar Prime is 2.91:1 on the dark canvas), while a green fill
 * stays Cedar Prime, because white on it is 6.33:1 in either theme. That split is
 * the reason a find-and-replace could never have built dark mode, and the reason
 * components name a role and never a colour.
 *
 * LIGHT IS TODAY'S APP. Every light value below is the existing constant itself
 * (`CoachCedarColors.x`, a `glassTokens` export), not a copy of its hex. Light
 * cannot drift from the shipped look by editing this file's dark half, and
 * `palette.light.test.ts` pins each role to the constant it replaces.
 *
 * DARK IS PROVISIONAL where it says so. On 2026-10-01 Matt had picked D4 (the room
 * is Cedar ink) and D5 (hero cards keep #113C2B). Every other dark value is this
 * plan's recommendation, tagged with the decision that owns it. They sit behind the
 * `dark_mode_enabled` flag, which is off for everyone; changing one is an edit to
 * this file and nowhere else.
 *
 * INVARIANT families (`glass`, `fill`, `data.hrZone`) are the same object in both
 * palettes, so the hero cards, quick views, dialogs and brand fills cannot differ
 * between themes. `palette.invariants.test.ts` holds that.
 *
 * ── Adding a role ────────────────────────────────────────────────────────────
 * Add it to `Palette`, to `light` (pointing at the existing constant) and to
 * `dark`. `palette.parity.test.ts` fails if the two halves disagree on a key, and
 * `palette.contrast.test.ts` wants a row for any new ink.
 */
import { CoachCedarColors as CC, HRZoneColors } from '../utils/styleConverter';
import {
  BUTTON_GRADIENT,
  CANVAS_FLOOR,
  CANVAS_GRADIENT,
  CEDAR_BRIGHT,
  CEDAR_FROST,
  CEDAR_FROST_INK,
  CTA_GRADIENT,
  CTA_GRADIENT_PRESSED,
  DAYLIGHT_ACCENT_INK,
  DETAIL_FLOATER,
  DETAIL_HEADER_EDGE,
  DETAIL_HEADER_GRADIENT,
  FROST_CARD,
  FROST_RAMP,
  GLASS_BG,
  GLASS_BORDER,
  GLASS_CARD,
  GLASS_CARD_LG,
  GLASS_CHIP_BG,
  GLASS_DATA_INK,
  GLASS_DIVIDER,
  GLASS_EDGE,
  GLASS_ERROR_INK,
  GLASS_HAIRLINE_SOFT,
  GLASS_LIFT,
  GLASS_LIFT_BG,
  GLASS_PANE_FALLBACK,
  GLASS_PANE_WASH,
  GLASS_ROOTS,
  GLASS_SHADOW,
  GLASS_TEXT,
  GLASS_TEXT_DIM,
  GLASS_TEXT_SECONDARY,
  NAV_GRADIENT,
  PILL_CREST,
  PILL_EDGE_INK,
  PILL_FOOT,
  PILL_GRADIENT,
  RECESSED_CARD,
  SCRIM,
  WALL_GRADIENT,
  SHARE_CARD_GROUND,
} from '../utils/glassTokens';
import type { Scheme } from './resolveScheme';
import { inkContrast, withAlpha } from './contrast';
import hrZoneTables from '../utils/hrZoneTables.json';
import { TIMER_DARK } from '../screens/components/timerPalette';

/** A linear gradient, in the shape expo-linear-gradient takes. */
export interface Gradient {
  colors: readonly [string, string, ...string[]];
  locations?: readonly [number, number, ...number[]];
  start: { x: number; y: number };
  end: { x: number; y: number };
}

/** A spreadable style fragment (`...t.material.frostCard`). */
export type StyleFragment = Readonly<Record<string, unknown>>;

/** Always-dark glass and what is written on it. Identical in both themes. */
export interface GlassRoles {
  bg: string;
  roots: string;
  bright: string;
  border: string;
  hairlineSoft: string;
  divider: string;
  liftBg: string;
  /** A neutral chip on glass (a status badge that is not live). */
  chipBg: string;
  text: string;
  textSecondary: string;
  textDim: string;
  /**
   * `textDim` laid over the glass as one opaque colour, for a mark whose fill and stroke
   * overlap (an SVG bolt: a see-through colour doubles where they meet), and the word beside
   * it: the PLAN pill of the onboarding bar before the plan is reached.
   */
  textDimSolid: string;
  dataInk: string;
  /**
   * A data mark on glass for something at risk, and for something behind: a leg's ring on
   * the Coach's Read, the road's line when the goal is behind. On track is `dataInk`.
   * They are marks and short tags, not the words of a problem (`warnInk`, `errorInk`).
   */
  dataAtRisk: string;
  dataBehind: string;
  /** An error mark on glass. The page's reds are too dark to read here. */
  errorInk: string;
  /**
   * The words of a chip on glass that is tinted thrive: how strong a study's evidence is, in
   * the research brief's dialog. A pale teal, as that dialog has always drawn it.
   */
  evidenceInk: string;
  /**
   * Cedar Mist on an always-dark green, and its tint: the moments the notifications ask lists,
   * each an icon on the mist at 14%, and on the brand's wall the pill that says the email check
   * is watching (a tint of it, its edge, its words) and a line that says something went well.
   */
  mist: string;
  /**
   * A text field on glass (the redeem-code dialog's): a white field in both themes, as the
   * alert's Cancel is a pale button in both. What is typed in it is `ink.onBright`.
   */
  field: string;
  /** The placeholder in such a field. */
  fieldHint: string;
  /** The edge of a banner on glass that reports a problem (the chat's sync banner, failed). */
  warnEdge: string;
  /** The words that name the problem on such a banner. */
  warnInk: string;
  /**
   * A whole strip on glass turned amber: the recalibration strip while Cedar waits on a reply.
   * Not drawn since 2026-06-03 (the strip is taken down in that state instead) and kept as that
   * decision's way back. White on it is 2.79:1: if the look returns, this is the value to deepen.
   */
  warnFill: string;
  /** The white a sheen is made of (InstrumentSheen): light on a surface, at its own opacity. */
  sheen: string;
  pillCrest: string;
  pillFoot: string;
  /** The lit hairline every ramped green surface carries (GLASS_EDGE's colour), for a border that is not a spread. */
  litEdge: string;
  /** What a button on glass does while it is held: a thrive edge and a small give. Spread it. */
  pressed: StyleFragment;
  edge: StyleFragment;
  shadow: StyleFragment;
  card: StyleFragment;
  cardLg: StyleFragment;
  lift: StyleFragment;
  ctaGradient: Gradient;
  buttonGradient: Gradient;
  pillGradient: Gradient;
}

/** Brand fills. A fill keeps its colour in dark; what is written on it is `ink.onFill`. */
export interface FillRoles {
  brand: string;
  brandDark: string;
  brandLight: string;
  thrive: string;
  thriveDeep: string;
  thriveHover: string;
  roots: string;
  /** The quiet button on dark glass (the alert's Cancel): a pale fill in both themes. */
  neutral: string;
  neutralPressed: string;
  /**
   * A pale pill, in both themes: today's word on the readiness strip, last night's score.
   * Its ink is `ink.onBright`; a readiness band on it is `data.readinessOnPale`.
   */
  pale: string;
  /** A destructive button. Its label is `ink.onFill`. */
  danger: string;
  dangerPressed: string;
  /**
   * A warning strip (the chat's "Payment issue" banner): an amber deep enough to hold white
   * words, 5.02:1. Its words are `ink.onFill`. Not `status.warning`, which is a bright amber
   * for marks and text and holds white at 2.15:1.
   */
  warning: string;
  /**
   * The bright amber as a strip, in both themes: the banner that says the connection is gone.
   * Its words and its mark are `ink.onBright`, 9.7:1.
   */
  warningBright: string;
}

export interface Palette {
  scheme: Scheme;

  /** Page grounds. */
  canvas: {
    /** The tab pages' canvas (white, settling to the floor). Flat in dark. */
    gradient: Gradient;
    /** The canvas's bottom stop. */
    floor: string;
    /** The flat page ground of the detail pages (limestone). */
    ground: string;
    /** A plain white page root. */
    page: string;
    /** The deck-mode nav pane. */
    nav: Gradient;
    /** A detail page's header bar: it leaves the page at the top and meets it at the bottom. */
    header: Gradient;
  };

  /** Fills that are surfaces: things content sits on. */
  surface: {
    /** A white card or bubble. */
    card: string;
    /** The barely-off-white panel (`backgroundLight`). */
    raised: string;
    /** Cedar Subtle: soft green panels and chips. */
    subtle: string;
    /** Limestone used as a control or chip fill (not as a page ground). */
    well: string;
    /**
     * A card while it is held down: the fill it takes in place of its own. In light it is
     * limestone, a step from white toward the page: the card recedes. In dark a card and a
     * well are the same lift, so a card that went to `well` when held would not change at
     * all. This is half a card's lift — the same step, toward the room — and what is on the
     * card reads a little better while it is held, not worse.
     */
    cardHeld: string;
    /** The empty / inactive placeholder grey. */
    neutral: string;
    /**
     * A loading skeleton's block (components/native/NativeSkeleton.tsx). A colour of its own
     * on purpose: borrowed from a border, it once vanished when the page under it changed.
     * It pulses between 60% and 100%, and is measured at both ends against the page: seen at
     * its faintest, still a placeholder at its strongest (NativeSkeleton.dark.test.tsx).
     */
    skeleton: string;
  };

  /** Sheets and drawers. */
  sheet: {
    /** The sheet's body under its Roots header. */
    body: string;
    /** The drag handle of a sheet that has no Roots header. */
    handle: string;
    /**
     * What ends a sheet that has no Roots header against the dimmed page. Spread
     * it. A zero-width, transparent border in light, where a white sheet on the
     * scrim needs no edge.
     */
    edge: StyleFragment;
    /**
     * A sheet that is LIGHT in both themes, filled with a light colour its caller was handed
     * (a campaign an admin drew on a light ground): its handle and its ×. Light's own, and
     * the same object in dark.
     */
    bright: { handle: string; close: string };
  };

  /** Composite materials, spread into a style. */
  material: {
    frostCard: StyleFragment;
    cedarFrost: StyleFragment;
    recessedCard: StyleFragment;
    frostRamp: Gradient;
    /** The glass pane's wash over real blur, top to bottom (the nav pill, the composer, the segmented strip). */
    glassWash: readonly [string, string];
    /** The glass pane's whole material where the phone cannot blur. */
    glassFallback: readonly [string, string];
    /** The small toolbar that floats over a detail header's edge (chat, share). */
    floater: StyleFragment;
    /**
     * The data panel inside an instrument: the week strip's panel and the week's summary on
     * the This Week card. Spread it. In light a solid white panel with no edge: colours that
     * carry meaning are read on a light ground. In dark the panel is set INTO the card — the
     * instrument's own green, ended by a hairline — and is not lifted off it.
     */
    dataPanel: StyleFragment;
  };

  /** Text and icon colours. */
  ink: {
    /** Body text. */
    primary: string;
    /** Dark Cedar used as text: headings and strong labels. */
    strong: string;
    /** Secondary text. */
    secondary: string;
    /** Hints and timestamps. */
    tertiary: string;
    /** Green words, links and icons. */
    accent: string;
    /** The softer green (Cedar Light) used as text. */
    accentSoft: string;
    /** White text on a brand or status fill. Stays white in dark. */
    onFill: string;
    /** The label on the primary button. */
    onCta: string;
    /** The only legible ink on `material.cedarFrost`. */
    onCedarFrost: string;
    /** The momentary flash on a nav icon while it is held. Never the colour of the selected one. */
    pressFlash: string;
    /**
     * Ink on a BRIGHT fill — thrive, limestone — in both themes. White fails there
     * (1.9:1 on thrive), so these labels are Cedar ink whatever the theme is.
     */
    onBright: string;
  };

  fill: FillRoles;

  /** The primary button. */
  cta: {
    /** Its base fill, under the gradient. */
    fill: string;
    gradient: Gradient;
    /** The 1pt edge. */
    edge: string;
    /** Whether the button also carries the top-down sheen. A ramped button does; a flat one does not. */
    lit: boolean;
  };

  /** Borders, dividers and hairlines. */
  line: {
    border: string;
    borderLight: string;
    borderDark: string;
    divider: string;
    /** The ink hairline that ends a frost card on a light page. */
    hairline: string;
    /** A border in the brand green. */
    brand: string;
    /** The ghosted outline of floating glass: the nav pill, the composer, the segmented strip. */
    edge: string;
    /** The hairline under a detail page's header bar. */
    headerEdge: string;
    /** The outline of the floater. None in light, where it is white with a shadow. */
    floaterEdge: string;
  };

  /** What a shadow is cast in. Never an ink role: ink is white in dark, and a white shadow is a glow. */
  shadow: {
    /** The Daylight shadow: cedar ink. */
    ink: string;
    /** The black shadow an overlay casts: a sheet, a dialog, a quick view, a toast. */
    deep: string;
  };

  /** The tab bars. */
  nav: {
    /** The selected glyph's vertical ramp, top to bottom. */
    glyphRamp: { from: string; to: string };
    /**
     * The mark cut out of the Cedar tab's tile. The tile is drawn in the tab's own
     * colour, so the mark has to read against whatever that is.
     */
    glyphKnockout: string;
  };

  status: {
    success: string;
    warning: string;
    /**
     * The stronger amber, for WORDS on a page, a card or a well: "At risk", a gap, a tag.
     * The bright amber is 2.15:1 on white: a mark or a fill there, never words. In dark the
     * bright amber is the one that reads, so the two are one colour.
     */
    warningStrong: string;
    error: string;
    /** The stronger red, used for text and destructive presses. */
    errorStrong: string;
    errorDeep: string;
    /** A soft red panel behind an error. */
    errorTint: string;
    /** A soft amber panel behind a caution: a chip, a notice. */
    warningTint: string;
    info: string;
  };

  /** Section-identity inks (eyebrows, labels, counts). */
  section: {
    drive: string;
    thrive: string;
    reset: string;
    roots: string;
  };

  /** Colours that carry meaning in data. */
  data: {
    hrZone: {
      zone1: string;
      zone2: string;
      zone3: string;
      zone4: string;
      zone5: string;
    };
    /** Rest-day and activity teal. Kept, by decision, through the Thrive migration. */
    rest: string;
    /**
     * The rest teal as WORDS: a rest day's pill, a warm-up's. The same teal in light. Lighter
     * in dark, where the teal itself is 4.4:1 on its own tint; the marks beside the words
     * (the battery, the chevron, a dot) stay `rest`.
     */
    restWords: string;
    restSky: string;
    restDay: string;
    /**
     * A past day on the week strip whose session was missed: its dot. Cedar Mist in light; in
     * dark the same mist, thinned, so that it is neither a day to come nor an empty one.
     * The Health tab draws what is BEHIND in it too, for the same reason: the far end of a
     * bar strip's ramp, the ring of a period that did not count, a phase already done.
     */
    dayMissed: string;
    /**
     * What kind of session it is — the colour of its icon, of its pill, of its dot on the week
     * strip and the calendar. Cardio is coloured by how hard it is; everything else by family.
     * `utils/nativeWorkoutIconMapper` says which session gets which.
     *
     * Cardio at a middling effort (base, a long run), and cardio as a whole when its sessions differ.
     */
    cardio: string;
    /** Hard cardio: intervals, tempo, race pace. HIIT and the fast sports share it. */
    cardioHard: string;
    /** Easy cardio: easy, recovery. The sports with no colour of their own share it. */
    cardioEasy: string;
    /** Strength: weights, bodyweight, core. (The token it comes from is named for yoga; only strength uses it.) */
    strength: string;
    /** Mobility and yoga, as a family of sessions. */
    mobility: string;
    /**
     * What kind of mobility routine it is, for the kinds that have a colour of their own: the
     * words of its pill and its icon, each on its own tint (the mobility card, and the
     * routine's page). The other kinds borrow: a stretch, a daily routine and corrective work
     * take the accent ink, a warm-up and a cool-down the rest teal.
     */
    routine: {
      yoga: string;
      /** Rehabilitation. The error red, as a kind of routine. */
      rehab: string;
      /** Self-myofascial release: foam rolling. */
      smr: string;
      /** Joint mobilisation. The success green, as a kind of routine. */
      joint: string;
    };
    /**
     * How ready the body is, as the colour of a day's dot: on the week's strip in Today's
     * instrument, on the day's card in the drawer, and a night's bar (a night's score is
     * painted on the same ramp). The WORD beside a dot says the band; the dot is a mark.
     * `components/health/healthTheme.ts` says which band gets which.
     */
    readiness: {
      peak: string;
      fresh: string;
      /** "Steady". */
      neutral: string;
      tired: string;
      depleted: string;
      /** No read: never the floor colour. */
      unknown: string;
    };
    /**
     * The same ramp as the dot on today's chip, which is a pale pill in both themes
     * (`fill.pale`): light's colours, in both. The twins dark gives the peak and a depleted
     * day are lifted to be seen on a dark ground; on the pill they are 2.45:1 and 2.42:1.
     */
    readinessOnPale: Palette['data']['readiness'];
    /**
     * A night's stages, as the segments of its bar in the last-night cell. The bar's track
     * is limestone in both themes (`fill.neutral`), so the segments do not change either.
     */
    sleepStage: { deep: string; rem: string; light: string; awake: string };
    /**
     * A night's stages on the drawer's sleep chart and on the bar of one night, which are
     * drawn on the chart's card and so follow the theme: the fill of each stage, and the ink
     * of a length written on it. `unstaged` is time the watch recorded and put in no stage.
     * Not `sleepStage`: the last-night cell has a table of its own, on a pale track.
     */
    sleepChart: Record<'deep' | 'rem' | 'light' | 'awake' | 'unstaged', { fill: string; ink: string }>;
    /**
     * How strong a study's evidence is, on its card in the Insights library: the rail down
     * the card's edge, and the chip that says it in a word — a tint of the rail (`chipBg`)
     * under an ink (`chipInk`). The rail is a mark, never a ground for words. In light the
     * strongest evidence is the deepest green; in dark it is the brightest.
     */
    evidence: Record<'strong' | 'moderate' | 'emerging', { rail: string; chipBg: string; chipInk: string }>;
    roadmapBadge: Record<'under_review' | 'planned' | 'in_progress' | 'shipped' | 'feature' | 'improvement' | 'known_issue' | 'neutral', { bg: string; fg: string }>;
    /**
     * Where a heart-rate number came from, as a chip beside it in the older Heart Rate Zones
     * drawer (`HRZonesTierBadge`): a lab test (success), a number the athlete set (warning), a
     * device or Cedar's own reading (info), a formula or a guess (neutral). In light each is a
     * solid fill under white (the neutral one under Cedar ink); in dark the word is the status
     * colour on a tint of it, as the zones drawer's chips are.
     */
    tierBadge: Record<'success' | 'warning' | 'info' | 'neutral', { bg: string; fg: string }>;
    /**
     * The four sections of the "What are heart rate zones?" primer, darkest to palest: each is
     * a card in a tone of its own (`bg`) with its header's words (`titleColor`), its chevron
     * (`iconColor`), its body (`bodyTextColor`), a quieter second line (`subtleTextColor`), a
     * row's name (`emphasisTextColor`) and the hairline under a row (`dividerColor`). In dark
     * the tones are the same four steps, from the strongest tint over the room to a card's lift.
     */
    explainerTone: Record<'cedarLight' | 'cedarMist' | 'cedarSubtle' | 'limestone', { bg: string; titleColor: string; iconColor: string; bodyTextColor: string; subtleTextColor: string; emphasisTextColor: string; dividerColor: string }>;
    zoneRamp: Record<'cedar_5' | 'garmin_5' | 'polarized_3' | 'friel_7', string[]>;
    /**
     * A heart-rate zone's colour as WORDS, and as the mark beside them: a phase's
     * "Z2 • 120–140 bpm" on the workout page. By the zone's place in the athlete's model:
     * `five` for a five-zone athlete (`hrZone`), and the Zones drawer's ramp for a three- or
     * a seven-zone one (`zoneRamp`). The zone's own colour in light. In dark the three that
     * cannot be read on a card take the theme's twin of that colour; a bar, a segment and a
     * dot keep the zone's own.
     */
    zoneWords: Record<'five' | 'polarized_3' | 'friel_7', string[]>;
    /**
     * What a zone's label is written in ON its bar, on the targets panel of a three- or a
     * seven-zone athlete. White in light, as on every bar there. In dark the ink for a
     * bright fill, and white on a bar that ink cannot be read on.
     */
    zoneBarLabel: Record<'polarized_3' | 'friel_7', string[]>;
    powerRamp: string[];
    effortRamp: string[];
    /**
     * The Cedar Report's charts, where the PHONE picks the colour: a bar or a line the
     * server named no colour for, the completion ring and its percentage, and the
     * heart-rate zones (the first five, and a zone past them). A series the server DID
     * colour is drawn in the colour it sent, which the phone does not retint.
     */
    reportChart: Record<'bar' | 'line' | 'completion' | 'zone1' | 'zone2' | 'zone3' | 'zone4' | 'zone5' | 'zoneOther', string>;
    /**
     * An event drawn ON a chart, by what it is: a tick across a sparkline, a line on the
     * drawer's chart, the dot of its legend.
     */
    annotation: {
      /** A milestone reached, or its state changed. */
      milestone: string;
      /** Cedar changed the plan. */
      planChange: string;
      /** A health signal: a check-in that moved, a flag Cedar raised. */
      healthSignal: string;
      raceDay: string;
      /** A kind the app does not know: still drawn. */
      unknown: string;
    };
    /**
     * The bar at the head of a phase on the workout page, by the kind of phase: a warm-up, an
     * easy or recovery stretch, tempo or threshold, an interval, a cool-down, a main lift, an
     * accessory, a steady or long run, and any other kind. A kind keeps its colour in dark.
     */
    phaseBar: Record<'warmup' | 'easy' | 'tempo' | 'interval' | 'cooldown' | 'main' | 'accessory' | 'steady' | 'other', string>;
    /** The icon of a strength block, by its kind. The block's badge is a tint of the same colour. */
    strengthBlock: Record<'activation' | 'main' | 'accessory' | 'cooldown', string>;
    /**
     * A phase on the targets panel of a workout that is not held to heart rate: the segment of
     * the bar that is its length, and the dot at the head of its row. Pace and power are
     * coloured by the ORDER the phases come in, the fifth colour serving every phase after
     * the fourth; effort by how hard it is out of ten: to 3, to 5, to 7, to 8, and above.
     */
    targetBar: { pace: string[]; power: string[]; effort: string[] };
    /**
     * A row of the zones card of a finished session: the dot at its head and the bar of its
     * share of the session. By what the session's time is cut into: a heart-rate zone (the
     * zone's own colour, first to fifth), a pace band (fastest to slowest), a power zone
     * (first to seventh; an eighth takes the seventh's). A row keeps its colour in dark.
     */
    timeInZone: { heartRate: string[]; pace: string[]; power: string[] };
    /**
     * A line of a finished session's timeline, by what the watch measured: its pill, its line
     * on the chart, the scale written beside it, and its dot under a finger and on its tile.
     * A line keeps its colour in dark, all but the violet of the cadence, the grade and the
     * vertical ratio.
     */
    timelineLine: Record<'hr' | 'pace' | 'cadence' | 'power' | 'altitude' | 'grade' | 'stride' | 'gct' | 'vert_osc' | 'gc_balance' | 'vert_ratio', string>;
    /**
     * The athlete's zones behind the heart rate on that timeline, first to fifth: a tint of
     * each zone's own colour, the fifth a little stronger. The same tints in dark, all but the
     * fifth, which is stronger still there.
     */
    zoneBand: string[];
    ownZoneBand: Record<'polarized_3' | 'friel_7', string[]>;
    /**
     * A segment of the strip of the time in each zone, under a tile of the timeline: a power
     * zone (first to seventh) and a pace band (fastest to slowest). A heart-rate zone's is
     * `timeInZone.heartRate`. A segment keeps its colour in dark, all but the two hardest
     * power zones.
     */
    zoneStrip: { power: string[]; pace: string[] };
    /**
     * A metric pill of a workout's page, and of a mobility session's band: how long, how far,
     * a pace, the calories. One hue, three times: its fill, the ink of its words and its icon,
     * and its edge. A dark ink on a pale fill in light; in dark the hue thinned to a tint,
     * under a brighter ink of it.
     */
    metricPill: Record<'purple' | 'orange' | 'teal' | 'blue' | 'red' | 'green' | 'gray', { bg: string; text: string; border: string }>;
    /**
     * The badge on a segment of a finished session, by how the effort ranked: the athlete's
     * best on it, its fastest ever by anyone, one of their three best, one of its ten fastest.
     * The badge's fill; its words are black or white in light, and Cedar ink in dark.
     */
    segmentRank: Record<'pr' | 'kom' | 'top3' | 'top10', string>;
    /**
     * How hard a week's sessions were rated, as the load card's effort block bands them: easy,
     * moderate, hard. The hard band's amber is that card's warm accent too: a week above its
     * target on the sessions block's bar.
     */
    effortBand: Record<'low' | 'mid' | 'high', string>;
    /**
     * How closely a phase of a finished session was done as planned, as the bar on the "How It
     * Went" card: met, near, short. The card is the hero glass in both themes, so the three are
     * the same in both.
     */
    adherenceBar: Record<'met' | 'near' | 'short', string>;
    /**
     * The week's chart on the Load & Recovery card: a day's bar, today's, the bar of a race
     * day, the hairline of a day with nothing, the placeholder while a load is worked out,
     * the two lines of the week's budget, and the 7-day total drawn over the bars. The 7-day
     * line is not a green on purpose: it shares the chart with the bars and not their axis.
     */
    loadChart: Record<'bar' | 'today' | 'raceDay' | 'empty' | 'pending' | 'budget' | 'sevenDay', string>;
    /**
     * The colour of a weather icon, by the kind of weather: on a workout's weather card, in its
     * hourly strip, and beside a finished session's conditions. `utils/weatherIconColor` says
     * which forecast code is which kind. The same in both themes.
     */
    weather: Record<'clear' | 'clearNight' | 'partlyCloudy' | 'cloud' | 'rain' | 'storm' | 'snow', string>;
    /**
     * A triathlon's discipline, as the chip at the head of a leg's row: on the table of a
     * race's legs (where a HYROX station is a discipline too) and on a finished brick's
     * panel. Each is the session colour of the same sport: the swim the easy green, the bike
     * the orange, the run the mauve, a station the strength purple. A mark on a card, so in
     * dark each is that colour's dark twin.
     */
    discipline: Record<'swimming' | 'cycling' | 'running' | 'station', string>;
    /**
     * A discipline as the FILL of a brick's split icon well on the workout card, under a
     * white glyph. A fill keeps its colour: the same in both themes.
     */
    disciplineTile: Record<'swimming' | 'cycling' | 'running', string>;
    /**
     * A partner's own colour, where a mark of where a session came from is drawn in it: the
     * source marks and the "Powered by" line of a finished session. A partner's colour is its
     * brand's, and the same in both themes. Where a partner has a mark for a dark ground,
     * the file picks it by the theme (`nike` / `nikeReversed`, `appleGlyph` /
     * `appleGlyphReversed`, and the partners' artwork files).
     */
    partner: Record<
      | 'garmin'
      | 'strava'
      | 'stravaLink'
      | 'appleHealth'
      | 'google'
      | 'peloton'
      | 'wahoo'
      | 'zwift'
      | 'zwiftFooter'
      | 'runna'
      | 'nike'
      | 'nikeReversed'
      | 'appleGlyph'
      | 'appleGlyphReversed',
      string
    >;
  };

  /** Chat. */
  chat: {
    /**
     * The outline of Cedar's bubble. The brand green in light. In dark the bubble is a frost
     * card and takes a card's hairline: the green bar on its left is what says "Cedar" (D14).
     */
    bubbleEdge: string;
    /**
     * Cedar's accent ink at rest: the wait copy between pulses, which rests at the strength the
     * thinking dots rest at (30%). Light is that 30% of Cedar Prime over the white bubble, as one
     * opaque colour. Dark is the dark accent at 30%, left translucent, so it is right over
     * whatever the dark bubble is.
     */
    accentAtRest: string;
  };

  overlay: {
    /** Behind every dialog, sheet and quick view. */
    scrim: string;
    /** The tap-to-dismiss layer behind the pop-up nav. Its opacity is animated; this is its colour. */
    backdrop: string;
  };

  /**
   * The note the workout timer asks for when a session ends ("How'd it go?"): a system-grey
   * dialog over the timer, so one object for both themes (D15), as the timer is.
   */
  timerNote: {
    /** The dialog's fill. */
    card: string;
    /** Its edge, and the edge of its field. */
    edge: string;
    /** The check that says the session is done, and its disc (the check at 15%). */
    done: string;
  };

  /**
   * The share preview over a finished workout: a near-black scrim, the round button that sends
   * the card, and the way out. It frames a card that is the same in both themes, so it is too.
   */
  share: {
    scrim: string;
    /** The button that sends the card. */
    send: string;
    /** The same button over a race's card. */
    sendRace: string;
    /** The mark on the button, and on the race's. */
    markOnSend: string;
    markOnSendRace: string;
    /** The way out: its round fill, and its mark. */
    close: string;
    closeInk: string;
  };

  /**
   * The share cards: the picture of a finished workout and the picture of a friend code, laid
   * out off screen and photographed for the phone's share sheet. An image that leaves the app
   * is the same picture whatever theme it was made in (decision D15), so this is one object in
   * both palettes. What is white on it is `ink.onFill`, and an exercise's tile is
   * `animation.stage`.
   */
  shareCard: {
    /** The workout card's ground: the deep green ramp, from the top left down. */
    ground: Gradient;
    /** Dark Cedar, under the ramp. */
    base: string;
    /** The panel the route, the plan and the exercises are laid in. */
    panel: string;
    /** The coach's summary, and the same over a race's card. */
    summary: string;
    summaryRace: string;
    /** The line between two rows, and between two stats. */
    hairline: string;
    /** The title, a stat, an exercise, the finish time. */
    title: string;
    /** The coach's summary, as words. */
    body: string;
    /** The date, the watch that recorded it, a detail, the route's start, an easy phase. */
    mist: string;
    /** A panel's label and a stat's. */
    label: string;
    /** The footer. */
    faint: string;
    /** Thrive: the glow, the sweep, the route's end, a tint and an edge of it. */
    accent: string;
    /** A race's colour, and a hard phase's. */
    heat: string;
    /** A race's words in heat, softened. */
    heatSoft: string;
    /** The words in the tag pill. */
    tagInk: string;
    /** The words on a race's badge. */
    badgeInk: string;
    /** The route's colour by its speed: the slow end, the centre, hard, the fast end. A planned phase that is work is the centre. */
    ramp: { slower: string; centre: string; heat: string; faster: string };
    /** The friend-code card: its ground, the code's panel and its edge, and its words. */
    referral: { ground: string; panel: string; edge: string; body: string; brand: string; label: string; faint: string };
  };

  /**
   * A campaign: an announcement, a notice or a survey the team sends. Its colours are an
   * admin's, set per campaign, and are drawn as set in both themes. Where the admin set a
   * ground and left an ink out, what falls back is drawn for THAT ground, so it does not
   * change with the theme either: these, and the roles that are one value in both palettes
   * (`ink.onBright`, `ink.onFill`, `fill.brand`, `fill.brandLight`, `fill.neutral`,
   * `glass.mist`, `onGlass`). A campaign with no ground set is drawn on the theme's page or
   * sheet, in the theme's inks.
   */
  campaign: {
    /** Grey words on an admin's light ground: the body, the second button, the × on its limestone circle. */
    inkOnLight: string;
    /** The dots of a carousel or a sequence on an admin's ground: one not shown (the one shown is `fill.brand`). */
    dot: string;
  };

  /**
   * An exercise's animation. Its figures are drawn for a light ground (their kit is Dark Cedar
   * and their hair near-black), so what they stand on is light in BOTH themes (decision D15),
   * and the view that opens one full screen is near-black in both.
   */
  animation: {
    /** The pale stage a figure is drawn on, and the square shown while its file is on its way. */
    stage: string;
    /** What the full-screen view lays over the app. */
    viewer: string;
  };

  /**
   * The brand's wall: the dark green an athlete meets before any setting of theirs is known.
   * The splash, the first launch, sign-in and the page that finishes it, the email check, the
   * name and consent pages, and the health disclaimer over it are drawn on it, the same in
   * BOTH themes (decision D15). What is written on it is in the roles that do not change
   * either: white (`ink.onFill`, `onGlass`), the glass inks, the brand fills and `ink.onBright`
   * on a thrive one, and these.
   */
  wall: {
    /** The ground: Cedar ink at both edges and dark cedar between, left to right. */
    ground: Gradient;
    /** Thrive as words and marks on the wall: a link in the consent, the cross over the disclaimer. */
    accent: string;
    /** Words that are a link, and the spinner a link turns into: "Create account", "Sign out". */
    link: string;
    /** "Sign In Failed" when a sign-in comes back with nothing. */
    failed: string;
    /** The red of a problem's panel on the name pages: a tint of it, and its edge. */
    problem: string;
    /** What dims the wall behind the health disclaimer's card. */
    scrim: string;
    /** Sign in with Apple: Apple's black button, and the grey it goes under a finger. */
    apple: string;
    applePressed: string;
    /** The four colours of Google's "G" on its button. */
    google: { red: string; blue: string; yellow: string; green: string };
    /**
     * The launch splash's own green, which picks up from the phone's launch screen: a radial
     * from the mark out, `centre` to `base` to `edge`. `base` is painted flat at once, under it.
     */
    splash: { centre: string; base: string; edge: string };
  };

  /**
   * The Legal screen at sign-up: a white page in BOTH themes (the plan's P9), where a new
   * athlete reads the terms of use and agrees. What is on it is in the roles that do not
   * change either: the brand fills and `ink.onFill` on them, `ink.onBright` and
   * `fill.brandDark` as words, and these.
   */
  legal: {
    /** The page, and the bar "I Agree" stands on. */
    page: string;
    /** The line above that bar. */
    edge: string;
    /** "Scroll to the bottom to continue". */
    hint: string;
    /** "I Agree" before the terms are read to the end: its fill, and its words. */
    waiting: string;
    waitingInk: string;
    /** What went wrong when agreeing. */
    error: string;
  };

  /**
   * The page of a link nothing in the app matches: a navy page with a blue button, the same in
   * BOTH themes. Its words are `ink.onFill`.
   */
  notFound: {
    page: string;
    /** The line under the title. */
    line: string;
    /** "Go Home". */
    button: string;
  };

  /**
   * The workout timer and the sheets it opens (the set log, the inline set ledger, the
   * strength completion screen): one dark instrument in BOTH themes (decision D15). The
   * timer's own palette (`screens/components/timerPalette.ts`), the very object, so the two
   * cannot drift; its contrast on the timer's three grounds is measured there.
   */
  timer: typeof TIMER_DARK;

  /**
   * The timer's light islands: the surfaces it lays on its dark instrument that are light in
   * BOTH themes (decision D15, the plan's P9) — the limestone sensor panels, the white sensor
   * sheet and the white milestone card — and what is written on them. Their Cedar ink is
   * `ink.onBright`, their buttons the `fill.*` roles: those are the same in both themes too.
   */
  island: {
    /** The white sheet and the white card. */
    surface: string;
    /** The limestone panel the sensor chips sit on, and a button that cannot be pressed. */
    panel: string;
    /** Secondary words and icons. */
    secondary: string;
    /** Hints, and what is not paired. */
    tertiary: string;
    /** A hairline between rows, the edge of a quiet button, and the white sheet's handle. */
    edge: string;
    /** What the milestone card lays over whatever it opens on. */
    scrim: string;
  };

  /** What a control the phone itself draws is handed for its parts. */
  control: {
    /** A switch that is on: its track. */
    switchOn: string;
    /** A switch that is off: its track. */
    switchOff: string;
    /** A switch's thumb, on either track. White in both themes. */
    switchThumb: string;
  };

  glass: GlassRoles;

  /** What the app tells the operating system and its own native controls. */
  system: {
    /** `<StatusBar barStyle>` over a themed ground. Always-dark screens pass `light-content` themselves. */
    statusBarStyle: 'dark-content' | 'light-content';
    /** Android's navigation-bar buttons, through `<SystemBars style={{ navigationBar }} />`. Always-dark screens pass `light` themselves. */
    navigationBarStyle: 'dark' | 'light';
    /** `TextInput keyboardAppearance`. */
    keyboardAppearance: 'light' | 'dark';
    /** `DateTimePicker themeVariant`. */
    pickerVariant: 'light' | 'dark';
    /** `BlurPane tint`. */
    blurTint: 'light' | 'dark';
  };
}

// ── Invariants: one object, used by both palettes ────────────────────────────

const glass: GlassRoles = {
  bg: GLASS_BG,
  roots: GLASS_ROOTS,
  bright: CEDAR_BRIGHT,
  border: GLASS_BORDER,
  hairlineSoft: GLASS_HAIRLINE_SOFT,
  divider: GLASS_DIVIDER,
  liftBg: GLASS_LIFT_BG,
  chipBg: GLASS_CHIP_BG,
  text: GLASS_TEXT,
  textSecondary: GLASS_TEXT_SECONDARY,
  textDim: GLASS_TEXT_DIM,
  textDimSolid: '#7C948A',
  dataInk: GLASS_DATA_INK,
  dataAtRisk: CC.warning,
  dataBehind: CC.error,
  errorInk: GLASS_ERROR_INK,
  evidenceInk: '#9FE1E1', // no constant: the research source's body wrote it out
  mist: CC.cedarMist,
  field: CC.surface,
  fieldHint: CC.textLight, // 4.83:1 on the field
  warnEdge: '#D38B3A',
  warnInk: '#FFB74D',
  warnFill: '#D38B3A',
  sheen: '#FFFFFF',
  pillCrest: PILL_CREST,
  pillFoot: PILL_FOOT,
  litEdge: GLASS_EDGE.borderColor,
  pressed: CTA_GRADIENT_PRESSED,
  edge: GLASS_EDGE,
  shadow: GLASS_SHADOW,
  card: GLASS_CARD,
  cardLg: GLASS_CARD_LG, // D5 = keep (Matt, 2026-09-29): the hero cards do not change in dark
  lift: GLASS_LIFT,
  ctaGradient: CTA_GRADIENT,
  buttonGradient: BUTTON_GRADIENT,
  pillGradient: PILL_GRADIENT,
};

const fill: FillRoles = {
  brand: CC.cedarPrime,
  brandDark: CC.darkCedar,
  brandLight: CC.cedarLight,
  thrive: CC.thrive,
  thriveDeep: CC.thriveDeep,
  thriveHover: CC.thriveHover,
  roots: GLASS_ROOTS,
  neutral: CC.limestone,
  neutralPressed: CC.neutralGray,
  pale: CC.cedarSubtle,
  danger: CC.error,
  dangerPressed: CC.errorDark,
  warning: '#B45309',
  warningBright: CC.warning,
};

/** A readiness band on a pale pill: one table for both themes, because the pill does not change. */
const readinessOnPale = {
  peak: CC.cedarPrime, // 5.53:1 on the pill
  fresh: CC.cedarLight, // 2.62:1: as in the app today
  neutral: CC.thrive, // 1.66:1: as today
  tired: CC.warning, // 1.88:1: as today
  depleted: CC.error, // 3.29:1
  unknown: CC.textLighter, // never drawn: a day with no read has no chip
};

/** A night's stages on their limestone track: one table for both themes. */
const sleepStage = {
  deep: CC.cedarPrime, // 5.71:1 on the track
  rem: CC.cedarStream, // 2.64:1: as in the app today
  light: CC.cedarLight, // 2.70:1: as today
  awake: CC.textLighter, // 2.29:1: as today
};

/** The note the timer asks for when a session ends: one object for both themes. The file wrote the three out. */
const timerNote = {
  card: '#1C1C1E',
  edge: '#38383A',
  done: '#34C759',
};

/** The share preview's chrome: one object for both themes. */
const share = {
  scrim: 'rgba(5, 13, 9, 0.82)',
  send: CC.thrive,
  sendRace: CC.heat,
  markOnSend: '#0E3123',
  markOnSendRace: '#2A1206',
  close: 'rgba(252, 252, 252, 0.14)',
  closeInk: CC.freshAir,
};

/** The share cards: one object for both themes (D15). The two files wrote these out. */
const shareCard: Palette['shareCard'] = {
  ground: SHARE_CARD_GROUND,
  base: '#113C2B',
  panel: '#0E2C20',
  summary: '#123B2B',
  summaryRace: '#26170F',
  hairline: 'rgba(159,201,179,0.16)',
  title: CC.freshAir,
  body: '#EAF6EF',
  mist: '#9FC9B3',
  label: '#6FA98F',
  faint: '#5E7B6D',
  accent: CC.thrive,
  heat: CC.heat,
  heatSoft: '#F0A98C',
  tagInk: '#BEEBD6',
  badgeInk: '#2A1206',
  ramp: { slower: '#C2AFF0', centre: '#2FA877', heat: CC.heat, faster: '#FF5666' },
  referral: { ground: '#0C2119', panel: '#071510', edge: '#1C3529', body: '#D6E4DC', brand: '#8FB3A2', label: '#5E7B6D', faint: '#4C6558' },
};

/** A campaign's fallbacks on a ground its admin set: one object for both themes. The three files wrote these out. */
const campaign: Palette['campaign'] = {
  inkOnLight: CC.textSecondary,
  dot: CC.borderDark,
};

/** A sheet light in both themes: light's handle and ×, one object for both. */
const brightSheet: Palette['sheet']['bright'] = {
  handle: CC.border,
  close: CC.textSecondary,
};

/** An exercise's animation, its stage and its full-screen view: one object for both themes. */
const animation = {
  stage: '#F0F7F4',
  viewer: 'rgba(0, 0, 0, 0.92)',
};

/** The brand's wall before sign-in: one object for both themes (D15). */
const wall: Palette['wall'] = {
  ground: WALL_GRADIENT,
  accent: CC.thrive,
  link: CC.cedarSubtle,
  failed: '#FF6B6B',
  problem: CC.errorDark,
  scrim: 'rgba(0,0,0,0.5)',
  apple: '#000000',
  applePressed: '#333333',
  google: { red: '#EA4335', blue: '#4285F4', yellow: '#FBBC05', green: '#34A853' },
  splash: { centre: '#1B6E4F', base: '#134D38', edge: CC.cedarInk },
};

/** The Legal screen at sign-up, white in both themes (P9): one object for both. */
const legal: Palette['legal'] = {
  page: CC.surface,
  edge: CC.border,
  hint: CC.textSecondary,
  waiting: CC.borderDark,
  waitingInk: CC.textTertiary,
  error: CC.error,
};

/** The page of a link to nowhere, the same in both themes: one object for both. */
const notFound: Palette['notFound'] = {
  page: '#1A1A2E',
  line: '#A0A0B0',
  button: '#4A90D9',
};

/** The timer's light islands: one object for both themes. */
const island = {
  surface: CC.surface,
  panel: CC.surfaceLight,
  secondary: CC.textSecondary,
  tertiary: CC.textTertiary,
  edge: CC.border,
  scrim: 'rgba(17, 60, 43, 0.7)',
};

/** A phase's bar and a strength block's icon, by kind, as the workout page and its icon table wrote them. */
const phaseBar = {
  warmup: '#fbbf24',
  easy: '#86efac',
  tempo: '#f97316',
  interval: '#ef4444',
  cooldown: '#93c5fd',
  main: '#8b5cf6',
  accessory: '#a78bfa',
  steady: '#22c55e',
  other: CC.primary,
};
const strengthBlock = {
  activation: '#F59E0B',
  main: CC.primary,
  accessory: CC.accent,
  cooldown: '#3B82F6',
};

/** A weather icon's colour, as the weather card and its hourly strip each wrote the seven: one object for both themes. */
const weather = {
  clear: '#F5A623',
  clearNight: '#7B8FA1',
  partlyCloudy: '#5A7A6C',
  cloud: '#8BA89B',
  rain: '#4A90D9',
  storm: '#7B6BA4',
  snow: '#6CB4D9',
};

/** A triathlon's disciplines, as the race table, the brick panel and the brick's well each wrote the three out. */
const disciplineTile = { swimming: CC.cedarPrime, cycling: CC.cardioOrange, running: '#A6496E' };

/**
 * A partner's own colour, as the source marks and the "Powered by" line wrote each: one object
 * for both themes. Zwift has two oranges because the two files wrote two. Strava's link back
 * to an activity is the orange its guidelines name, `#FC5200`.
 */
const partner = {
  garmin: '#007DC3',
  strava: '#FC4C02',
  stravaLink: '#FC5200',
  appleHealth: '#FF2D55',
  google: '#4285F4',
  peloton: '#E12726',
  wahoo: '#0066CC',
  zwift: '#F26522',
  zwiftFooter: '#FF6600',
  runna: '#00C48C',
  nike: '#111111',
  nikeReversed: '#FFFFFF',
  appleGlyph: '#333333',
  appleGlyphReversed: '#FFFFFF',
};

/** A segment's rank badge, as the segments card wrote the four. */
const segmentRank = { pr: '#F59E0B', kom: '#8B5CF6', top3: '#F97316', top10: '#3B82F6' };

/** A week's ratings in three bands, as the load card's effort block wrote them. None had a token. */
const effortBand = { low: '#7BBF9E', mid: '#5C9DBE', high: '#D69453' };

/** A phase's adherence bar on the hero glass, as the "How It Went" card wrote the three. The amber and the soft red had no token. */
const adherenceBar = { met: CC.thrive, near: '#D4A843', short: '#E8776A' };

/** The week's chart of the load card, as the card wrote the seven. Only the bar's green had a token. */
const loadChart = { bar: CC.cedarLight, today: '#2E7D5B', raceDay: '#D69453', empty: 'rgba(0,0,0,0.08)', pending: 'rgba(0,0,0,0.18)', budget: 'rgba(0,0,0,0.45)', sevenDay: '#B4703A' };

/** The metric pills, as the pills' own file wrote them. */
const metricPill = {
  purple: { bg: '#FAF5FF', text: '#6B21A8', border: '#E9D5FF' },
  orange: { bg: '#FFF7ED', text: '#C2410C', border: '#FED7AA' },
  teal: { bg: '#F0FDFA', text: '#0F766E', border: '#99F6E4' },
  blue: { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' },
  red: { bg: '#FEF2F2', text: '#B91C1C', border: '#FECACA' },
  green: { bg: '#F0FDF4', text: '#15803D', border: '#BBF7D0' },
  gray: { bg: '#F9FAFB', text: '#374151', border: '#E5E7EB' },
};

/** The targets panel's bars, as the panel wrote them. */
const targetBar = {
  pace: ['#60A5FA', '#06B6D4', '#22C55E', '#EAB308', '#F97316'],
  power: ['#C084FC', '#A855F7', '#EC4899', '#F43F5E', '#EF4444'],
  effort: ['#4ADE80', '#3B82F6', '#EAB308', '#F97316', '#EF4444'],
};

const hrZone = {
  zone1: HRZoneColors.zone1,
  zone2: HRZoneColors.zone2,
  zone3: HRZoneColors.zone3,
  zone4: HRZoneColors.zone4,
  zone5: HRZoneColors.zone5,
};

/** The zones card's dots and bars: the zones' own colours as a list, and its pace bands and power zones as the card wrote them. */
const timeInZone = {
  heartRate: [hrZone.zone1, hrZone.zone2, hrZone.zone3, hrZone.zone4, hrZone.zone5],
  pace: ['#60A5FA', '#06B6D4', '#10B981', '#F59E0B', '#EF4444'],
  power: ['#A78BFA', '#8B5CF6', '#EC4899', '#F43F5E', '#EF4444', '#DC2626', '#991B1B'],
};

/** The timeline's lines, as the timeline wrote them: five from the charts' tokens and six of its own. */
const timelineLine = {
  hr: '#EF4444',
  pace: '#3B82F6',
  cadence: '#8B5CF6',
  power: '#F59E0B',
  altitude: '#22C55E',
  grade: '#8B5CF6',
  stride: '#F59E0B',
  gct: '#EC4899',
  vert_osc: '#14B8A6',
  gc_balance: '#10B981',
  vert_ratio: '#8B5CF6',
};
/** The zones behind the heart rate: each zone's colour at an eighth (`20` of 255 as the timeline wrote it), the fifth's at a little more (`30`). */
const zoneBand = [withAlpha(hrZone.zone1, 0.125), withAlpha(hrZone.zone2, 0.125), withAlpha(hrZone.zone3, 0.125), withAlpha(hrZone.zone4, 0.125), withAlpha(hrZone.zone5, 0.188)];
/** The strip of the time in each zone: a power zone is a heart-rate zone's colour for the first five, then a burnt orange and a dark red; a pace band is the heart-rate zones turned round. */
const zoneStrip = {
  power: [...timeInZone.heartRate, '#C2410C', '#7F1D1D'],
  pace: [...timeInZone.heartRate].reverse(),
};

/**
 * The Cedar Report's charts: one table for both themes. PROVISIONAL in dark, and not on the
 * mock (it draws no chart): kept, because each is a mark on the chart's card and reads there
 * in dark as it is (3:1 or better; the numbers are for a chart card in the report's sheet),
 * and because the two series colours are the ones the SERVER sends with the load chart and
 * the sleep chart, which arrive as they are in either theme. They are the report's own
 * (`CHART_COLORS`, components/native/charts/chartUtils.ts): the zones here are not the app's
 * zone colours (`hrZone`), and the blue is the one the momentum ring gave up in July.
 */
const reportChart = {
  bar: '#3B82F6',
  line: '#8B5CF6',
  // The teal: the ring, and its percentage in words. Under its floor on limestone both ways
  // (as in the app today); in dark it reads as both.
  completion: CC.accent,
  // Grey, blue, green, yellow, red. On limestone only the blue and the red reach 3:1 (as in
  // the app today); in dark every one does. The numbers are in palette.contrast.test.ts.
  zone1: '#9CA3AF',
  zone2: '#3B82F6',
  zone3: '#22C55E',
  zone4: '#EAB308',
  zone5: '#EF4444',
  zoneOther: '#9CA3AF',
};

const zoneRamp = hrZoneTables.zone_chart_colors;

const ownZoneBandOf = (ramp: readonly string[]): string[] => ramp.map((zone, nth) => withAlpha(zone, nth === ramp.length - 1 ? 0.188 : 0.125));
const ownZoneBand = { polarized_3: ownZoneBandOf(zoneRamp.polarized_3), friel_7: ownZoneBandOf(zoneRamp.friel_7) };

const zoneWords = {
  five: [hrZone.zone1, hrZone.zone2, hrZone.zone3, hrZone.zone4, hrZone.zone5],
  polarized_3: zoneRamp.polarized_3,
  friel_7: zoneRamp.friel_7,
};

const zoneBarLabel = {
  polarized_3: zoneRamp.polarized_3.map(() => CC.surface),
  friel_7: zoneRamp.friel_7.map(() => CC.surface),
};

// PROVISIONAL. The dark theme's twin of a zone colour that is under 4.5:1 as words on a dark
// card: the blue (4.06:1) is the theme's info blue, the red (3.97:1) its error red (D12), and
// the seventh zone's deep red (2.31:1) the red a metric pill is written in.
const ZONE_WORDS_IN_DARK: Record<string, string> = { '#3B82F6': '#60A5FA', '#EF4444': '#F87171', '#B91C1C': '#FCA5A5' };
const zoneWordsInDark = (zones: readonly string[]): string[] => zones.map((zone) => ZONE_WORDS_IN_DARK[zone] ?? zone);
// PROVISIONAL. Cedar ink on a bar, as on the five-zone panel's; white where white reads better (the deep red).
const zoneBarLabelInDark = (bars: readonly string[]): string[] =>
  bars.map((bar) => (inkContrast(CC.surface, [bar]) > inkContrast(CC.cedarInk, [bar]) ? CC.surface : CC.cedarInk));

const powerRamp = ['#9CA3AF', '#3B82F6', '#22C55E', '#EAB308', '#F97316', '#EF4444', '#B91C1C'];

const effortRamp = ['#D8EEE3', '#C4E7D4', '#A9DCC2', '#7ED4A9', '#3BD49B', '#22C55E', '#E8A487', '#D0603E', '#EF4444', '#B91C1C'];

// ── Light: today's app ───────────────────────────────────────────────────────

export const light: Palette = {
  scheme: 'light',
  canvas: {
    gradient: CANVAS_GRADIENT,
    floor: CANVAS_FLOOR,
    ground: CC.limestone,
    page: CC.surface,
    nav: NAV_GRADIENT,
    header: DETAIL_HEADER_GRADIENT,
  },
  surface: {
    card: CC.surface,
    raised: CC.backgroundLight,
    subtle: CC.cedarSubtle,
    well: CC.limestone,
    cardHeld: CC.limestone,
    neutral: CC.neutralGray,
    skeleton: '#D5D9DE', // 1.10:1 to 1.18:1 on the canvas floor over its pulse; 1.23:1 to 1.42:1 on white
  },
  sheet: {
    body: CC.surface,
    handle: CC.border,
    // The same keys dark has (the two palettes are one shape), and none of them
    // paints or takes room: no width, no colour.
    edge: { borderWidth: 0, borderColor: 'transparent', borderBottomWidth: 0 },
    bright: brightSheet,
  },
  material: {
    frostCard: FROST_CARD,
    cedarFrost: CEDAR_FROST,
    recessedCard: RECESSED_CARD,
    frostRamp: FROST_RAMP,
    glassWash: GLASS_PANE_WASH,
    glassFallback: GLASS_PANE_FALLBACK,
    floater: DETAIL_FLOATER,
    // No constant: the This Week card wrote its two panels as the literal '#FFFFFF', with no border.
    dataPanel: { backgroundColor: CC.surface, borderWidth: 0, borderColor: 'transparent' },
  },
  ink: {
    primary: CC.cedarInk,
    strong: CC.darkCedar,
    secondary: CC.textSecondary,
    tertiary: CC.textTertiary,
    accent: CC.cedarPrime,
    accentSoft: CC.cedarLight,
    onFill: CC.surface,
    onCta: CC.surface,
    onCedarFrost: CEDAR_FROST_INK,
    pressFlash: CC.thrive,
    onBright: CC.cedarInk,
  },
  fill,
  cta: {
    fill: CC.cedarPrime,
    gradient: BUTTON_GRADIENT,
    edge: GLASS_EDGE.borderColor,
    lit: true,
  },
  line: {
    border: CC.border,
    borderLight: CC.borderLight,
    borderDark: CC.borderDark,
    divider: CC.divider,
    hairline: FROST_CARD.borderColor,
    brand: CC.cedarPrime,
    edge: PILL_EDGE_INK,
    headerEdge: DETAIL_HEADER_EDGE,
    floaterEdge: 'transparent',
  },
  shadow: {
    ink: CC.cedarInk,
    deep: '#000',
  },
  nav: {
    glyphRamp: { from: GLASS_ROOTS, to: CEDAR_BRIGHT },
    glyphKnockout: CC.surface,
  },
  status: {
    success: CC.success,
    warning: CC.warning,
    warningStrong: '#B45309', // 5.0:1 on white, 4.5:1 on limestone
    error: CC.error,
    errorStrong: CC.errorDark,
    errorDeep: '#B91C1C',
    errorTint: CC.errorLight,
    warningTint: '#FEF3C7', // no constant: the focus card's chip and ten other files each wrote this amber out
    info: CC.info,
  },
  section: {
    drive: DAYLIGHT_ACCENT_INK.drive,
    thrive: DAYLIGHT_ACCENT_INK.thrive,
    reset: DAYLIGHT_ACCENT_INK.reset,
    roots: DAYLIGHT_ACCENT_INK.roots,
  },
  data: {
    hrZone,
    rest: CC.cedarStream,
    restWords: CC.cedarStream,
    restSky: CC.accentSky,
    restDay: CC.restDay,
    dayMissed: CC.cedarMist,
    cardio: CC.cardioOrange,
    cardioHard: '#A6496E', // the mauve; it had no token: the icon mapper and four other files each wrote it out
    cardioEasy: CC.cedarPrime,
    strength: CC.yogaPurple,
    mobility: CC.cedarLight,
    routine: {
      yoga: '#8B5CF6', // no token: the mobility card and the routine's page each wrote it out
      rehab: CC.error,
      smr: '#EC4899', // no token either
      joint: CC.success,
    },
    readiness: {
      peak: CC.cedarPrime,
      fresh: CC.cedarLight,
      neutral: CC.thrive,
      tired: CC.warning,
      depleted: CC.error,
      unknown: CC.textLighter,
    },
    readinessOnPale,
    sleepStage,
    // As the drawer's sleep chart wrote them out: the deepest green for the deepest sleep.
    sleepChart: {
      deep: { fill: CC.roots, ink: CC.surface }, // 10.51:1 on the chart's card; its length 11.67:1
      rem: { fill: CC.cedarPrime, ink: CC.surface }, // 5.71:1; 6.33:1
      light: { fill: CC.cedarMist, ink: CC.cedarInk }, // 1.51:1: as in the app today; 11.01:1
      awake: { fill: CC.warning, ink: CC.surface }, // 1.94:1: as today; its length 2.15:1: as today
      unstaged: { fill: CC.limestone, ink: CC.cedarInk }, // the card's own colour: as today; 16.61:1
    },
    // As the Insights library wrote them out: Cedar Prime, the bright green, Cedar Light, each
    // under a tint of itself with the dark cedar on it.
    evidence: {
      strong: { rail: CC.cedarPrime, chipBg: 'rgba(31,108,79,0.16)', chipInk: CC.cedarDark },
      moderate: { rail: CEDAR_BRIGHT, chipBg: 'rgba(29,134,88,0.14)', chipInk: CC.cedarDark },
      emerging: { rail: CC.cedarLight, chipBg: 'rgba(95,162,135,0.18)', chipInk: CC.cedarDark },
    },
    roadmapBadge: {
      under_review: { bg: 'rgba(11, 22, 20, 0.08)', fg: CC.cedarInk },
      planned: { bg: 'rgba(168, 209, 191, 0.35)', fg: CC.cedarPrime },
      in_progress: { bg: 'rgba(59, 212, 155, 0.18)', fg: CC.cedarPrime },
      shipped: { bg: 'rgba(59, 212, 155, 0.14)', fg: CC.cedarPrime },
      feature: { bg: 'rgba(31, 108, 79, 0.10)', fg: CC.cedarPrime },
      improvement: { bg: 'rgba(59, 212, 155, 0.12)', fg: CC.cedarPrime },
      known_issue: { bg: 'rgba(217, 147, 43, 0.12)', fg: '#8B5A12' },
      neutral: { bg: 'rgba(11, 22, 20, 0.08)', fg: CC.cedarInk },
    },
    // No constant: the badge's own file wrote the four out in a table.
    tierBadge: {
      success: { bg: CC.success, fg: CC.surface },
      warning: { bg: CC.heat, fg: CC.surface },
      info: { bg: CC.info, fg: CC.surface },
      neutral: { bg: CC.thrive, fg: CC.cedarInk },
    },
    // As the primer wrote them out (components/native/education/WhatAreZones.native.tsx).
    explainerTone: {
      cedarLight: { bg: CC.cedarLight, titleColor: CC.surface, iconColor: CC.surface, bodyTextColor: CC.surface, subtleTextColor: 'rgba(255,255,255,0.85)', emphasisTextColor: CC.surface, dividerColor: 'rgba(255,255,255,0.3)' },
      cedarMist: { bg: CC.cedarMist, titleColor: CC.cedarPrime, iconColor: CC.cedarPrime, bodyTextColor: CC.primaryDark, subtleTextColor: CC.textSecondary, emphasisTextColor: CC.cedarPrime, dividerColor: 'rgba(31,108,79,0.2)' },
      cedarSubtle: { bg: CC.cedarSubtle, titleColor: CC.primaryDark, iconColor: CC.cedarPrime, bodyTextColor: CC.primaryDark, subtleTextColor: CC.textSecondary, emphasisTextColor: CC.primary, dividerColor: CC.cedarMist },
      limestone: { bg: CC.limestone, titleColor: CC.primaryDark, iconColor: CC.cedarPrime, bodyTextColor: CC.primaryDark, subtleTextColor: CC.textSecondary, emphasisTextColor: CC.primary, dividerColor: CC.cedarMist },
    },
    reportChart,
    zoneRamp,
    zoneWords,
    zoneBarLabel,
    powerRamp,
    effortRamp,
    annotation: {
      milestone: CC.cedarPrime,
      planChange: CC.heat,
      healthSignal: CC.warning,
      raceDay: CC.roots,
      unknown: CC.textLighter,
    },
    phaseBar,
    strengthBlock,
    targetBar,
    timeInZone,
    timelineLine,
    zoneBand,
    ownZoneBand,
    zoneStrip,
    metricPill,
    segmentRank,
    effortBand,
    adherenceBar,
    loadChart,
    weather,
    discipline: { ...disciplineTile, station: CC.yogaPurple },
    disciplineTile,
    partner,
  },
  chat: {
    bubbleEdge: CC.cedarPrime,
    accentAtRest: CC.cedarPrimeAtRest,
  },
  overlay: {
    scrim: SCRIM,
    backdrop: '#000000',
  },
  timerNote,
  share,
  shareCard,
  campaign,
  animation,
  wall,
  legal,
  notFound,
  timer: TIMER_DARK,
  island,
  control: {
    switchOn: CC.cedarPrime,
    switchOff: CC.border,
    switchThumb: CC.surface,
  },
  glass,
  system: {
    statusBarStyle: 'dark-content',
    navigationBarStyle: 'dark',
    keyboardAppearance: 'light',
    pickerVariant: 'light',
    blurTint: 'light',
  },
};

// ── Dark ─────────────────────────────────────────────────────────────────────

/** D4 = B (Matt, 2026-09-29): the room is Cedar ink, the brand's own darkest colour. */
const DARK_ROOM = CC.cedarInk;
/** D6, PROVISIONAL (recommended 8%): the white lift that makes a secondary card. */
const DARK_FROST = 'rgba(255,255,255,0.08)';
/** The 1pt edge every dark surface carries; light frost carries an ink hairline in the same place. */
const DARK_EDGE = 'rgba(255,255,255,0.10)';
/**
 * PROVISIONAL, as mocked on the decision board (`--s-glass`): floating glass — the
 * nav pill, the composer, the segmented strip — is a card's lift plus 2%, over the blur.
 */
const DARK_GLASS = 'rgba(255,255,255,0.10)';
/**
 * The same pane where the phone cannot blur. A 10% wash would let the content under
 * it show through unblurred, so this is the colour that wash makes over the room
 * (`#0B1614` + white 10% = rgb(35,45,44)), made dense — the job light's 70% wash does.
 */
const DARK_GLASS_DENSE = 'rgba(35,45,44,0.92)';

export const dark: Palette = {
  scheme: 'dark',
  canvas: {
    // Flat. The light canvas falls from white to its floor; whether the dark room
    // falls too is the design agent's call in the P0 mock (see D4).
    gradient: {
      colors: [DARK_ROOM, DARK_ROOM, DARK_ROOM],
      locations: CANVAS_GRADIENT.locations,
      start: CANVAS_GRADIENT.start,
      end: CANVAS_GRADIENT.end,
    },
    floor: DARK_ROOM,
    ground: DARK_ROOM,
    page: DARK_ROOM,
    nav: {
      colors: [DARK_ROOM, DARK_ROOM],
      start: NAV_GRADIENT.start,
      end: NAV_GRADIENT.end,
    },
    // PROVISIONAL, as mocked on the decision board (`--s-dh-bg`): the room. What
    // separates the bar from the page in dark is its edge (`line.headerEdge`).
    header: {
      colors: [DARK_ROOM, DARK_ROOM],
      start: DETAIL_HEADER_GRADIENT.start,
      end: DETAIL_HEADER_GRADIENT.end,
    },
  },
  surface: {
    card: DARK_FROST, // D6
    raised: 'rgba(255,255,255,0.06)',
    subtle: 'rgba(59,212,155,0.12)', // thrive at 12%: Cedar Subtle's job on a dark ground
    well: DARK_FROST,
    cardHeld: 'rgba(255,255,255,0.04)', // PROVISIONAL: half a card's lift, the size of step white to limestone is (palette.test.ts holds the two together)
    neutral: 'rgba(255,255,255,0.16)',
    // PROVISIONAL (the mockups draw no loading state): 1.16:1 to 1.30:1 on the room over its
    // pulse, between what light's is on the canvas floor and on white. A card's own lift (8%)
    // is 1.12:1 at the trough.
    skeleton: 'rgba(255,255,255,0.10)',
  },
  sheet: {
    // D8, PROVISIONAL — option (a): the Roots header stays, and the body under it is
    // the room. That is light's own shape (a Roots header over a body the colour of
    // the page), and it means every ink measured for a page holds inside a drawer.
    //
    // It was option (b), Roots top to bottom, until the render audit measured it:
    // Roots is 1.58x lighter than the room, and on it hints (4.19:1), error text
    // (4.22), the Drive accent (3.76) and, on a panel inside it, secondary text
    // (4.42), warning, success and info all fall under 4.5:1. Roots top to bottom
    // would need a second, brighter set of inks for everything inside a drawer.
    // palette.contrast.test.ts holds both halves of that: the drawer rows pass on
    // this body, and the same roles are shown to fail on Roots.
    body: DARK_ROOM,
    // PROVISIONAL, as mocked on the decision board (`--s-handle`).
    handle: 'rgba(255,255,255,0.30)',
    // PROVISIONAL, and NOT on the mock (it draws only Roots drawers). A sheet with no
    // Roots header is the room's own colour, and against the dimmed page that is
    // 1.08:1 — its top edge cannot be seen. So it carries the 1pt edge every dark
    // surface carries (1.40:1 against the dimmed page), on the three sides a Roots
    // sheet carries its lit hairline.
    edge: { borderWidth: 1, borderColor: DARK_EDGE, borderBottomWidth: 0 },
    bright: brightSheet,
  },
  material: {
    // Every key light has, so a spread lays out identically. No shadow and
    // `elevation: 0`: a translucent surface with Android elevation draws the
    // inset box (#3469), and a shadow on a dark ground is invisible anyway.
    frostCard: {
      backgroundColor: DARK_FROST,
      borderWidth: 1,
      borderColor: DARK_EDGE,
      borderRadius: 14,
      shadowColor: CC.cedarInk,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0,
      shadowRadius: 8,
      elevation: 0,
    },
    cedarFrost: {
      backgroundColor: 'rgba(59,212,155,0.12)',
      borderWidth: 1,
      borderColor: DARK_EDGE,
      borderRadius: 12,
      shadowColor: CC.cedarInk,
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 0,
      shadowRadius: 10,
      elevation: 0,
    },
    // D7, PROVISIONAL (recommended): a well BELOW the room, the tier's logic in light.
    recessedCard: {
      backgroundColor: 'rgba(0,0,0,0.28)',
      borderWidth: 1,
      borderColor: 'rgba(255,255,255,0.06)',
      borderRadius: 12,
    },
    frostRamp: {
      colors: ['rgba(59,212,155,0.06)', 'rgba(59,212,155,0)'],
      start: FROST_RAMP.start,
      end: FROST_RAMP.end,
    },
    // PROVISIONAL (see DARK_GLASS). Flat, like light's no-blur wash: a pane that is
    // one colour top to bottom has no seam where it meets the composer above it.
    glassWash: [DARK_GLASS, DARK_GLASS],
    glassFallback: [DARK_GLASS_DENSE, DARK_GLASS_DENSE],
    // PROVISIONAL, as mocked on the decision board (`--s-floater`: frost, no shadow).
    // Every key light has, so the pill lays out identically. `elevation: 0` because a
    // translucent surface with Android elevation draws the inset box (#3469).
    floater: {
      backgroundColor: DARK_FROST,
      shadowColor: CC.cedarInk,
      shadowOffset: DETAIL_FLOATER.shadowOffset,
      shadowOpacity: 0,
      shadowRadius: DETAIL_FLOATER.shadowRadius,
      elevation: 0,
    },
    // D13, PROVISIONAL, and NOT the mock's panel. The mock lifts it (white 7%, edge white 10%);
    // on that lift three of the colours the summary's words take are under 4.5:1 (the middling
    // orange 3.95, the hard mauve 4.11, the strength purple 4.06), before the sheen behind it
    // lightens it further. Set into the card, on the hero-card green, every one reads (4.9:1 or
    // better): the This Week card's dark test measures them. The edge is the mock's.
    dataPanel: { backgroundColor: GLASS_BG, borderWidth: 1, borderColor: DARK_EDGE },
  },
  ink: {
    primary: GLASS_TEXT, // white .92 — 15.6:1 on the room, 12.9:1 on a card
    strong: GLASS_TEXT,
    secondary: GLASS_TEXT_SECONDARY, // white .60 — 7.1:1 / 6.3:1
    // white .50, not the glass DIM (.45): .45 is 4.21:1 on a card, below AA for the
    // timestamps and hints this role carries. .50 is 4.86:1 there.
    tertiary: 'rgba(255,255,255,0.50)',
    accent: CC.thrive, // D10, PROVISIONAL (recommended): 9.7:1; Cedar Prime is 2.91:1
    accentSoft: CC.cedarLight, // 6.2:1 on the room, 5.0:1 on a card
    onFill: CC.surface,
    onCta: CC.cedarInk, // D11: 9.7:1 on the thrive button
    onCedarFrost: GLASS_TEXT,
    // PROVISIONAL. In light the flash is thrive against a Cedar Prime selection. In dark
    // the selection IS thrive (D10), so the flash is the lighter mint.
    pressFlash: CC.thriveHover,
    onBright: CC.cedarInk,
  },
  fill,
  cta: {
    // D11, PROVISIONAL (recommended b): the house button on dark glass — thrive fill,
    // ink label. The light ramp's dark end melts into the room at 1.58:1.
    fill: CC.thrive,
    gradient: {
      colors: [CC.thrive, CC.thrive],
      start: BUTTON_GRADIENT.start,
      end: BUTTON_GRADIENT.end,
    },
    edge: 'rgba(255,255,255,0)',
    // PROVISIONAL, as mocked on the decision board (`--s-cta-sheen: none`).
    lit: false,
  },
  line: {
    border: 'rgba(255,255,255,0.12)',
    borderLight: 'rgba(255,255,255,0.08)',
    borderDark: 'rgba(255,255,255,0.20)',
    divider: 'rgba(255,255,255,0.08)',
    hairline: DARK_EDGE,
    brand: 'rgba(59,212,155,0.40)',
    edge: DARK_EDGE,
    headerEdge: DARK_EDGE, // as mocked (`--s-dh-edge`)
    floaterEdge: 'rgba(255,255,255,0.12)', // as mocked (`--s-floater-edge`)
  },
  shadow: {
    // The same ink. A shadow on the dark room is invisible, which is the point: in
    // dark, lift comes from the fill, not from a shadow.
    ink: CC.cedarInk,
    deep: '#000',
  },
  nav: {
    // PROVISIONAL, as mocked on the decision board (`--s-tab-on-fill`): the selected
    // glyph is filled flat in the dark accent. Light's ramp starts at Roots, which is
    // 1.58:1 on the dark room — the top of a selected glyph would vanish.
    glyphRamp: { from: CC.thrive, to: CC.thrive },
    // PROVISIONAL, as mocked on the decision board (`--s-tab-cedar-tree`). In dark the
    // Cedar tile is pale at rest (white .60) and mint when selected, and a white mark is
    // 2.3:1 or worse on both (1.9 when selected). The mark is cut out to the room
    // instead: 7.9:1 or better on every tile the tab draws.
    glyphKnockout: DARK_ROOM,
  },
  status: {
    success: CC.success, // 7.3:1 on the room, 5.9:1 on a card
    warning: CC.warning, // 8.6:1 / 7.0:1
    warningStrong: CC.warning, // the bright amber is already the one that reads on dark
    // D12, PROVISIONAL: #EF4444 is 3.97:1 on a dark card; #F87171 is 5.4:1.
    error: '#F87171',
    errorStrong: '#F87171',
    errorDeep: '#F87171',
    errorTint: 'rgba(248,113,113,0.16)',
    warningTint: 'rgba(245,158,11,0.16)', // PROVISIONAL: the amber at the strength the error tint has
    info: '#60A5FA', // 7.3:1 / 5.9:1; #3B82F6 is 5.0:1 / 4.1:1
  },
  section: {
    // D12, PROVISIONAL (recommended): the raw brand accents. The darkened inks light
    // uses exist because these fail on white; on dark it flips.
    drive: CC.drive, // 6.0:1 on the room
    thrive: CC.thrive, // 9.7:1
    reset: CC.reset, // 9.4:1
    roots: CC.cedarMist, // 11.0:1 — Roots has no raw accent that reads on dark (#4E6459 is 2.89:1)
  },
  data: {
    hrZone,
    rest: CC.cedarStream, // 6.3:1 on the room; the teal is kept
    // PROVISIONAL, as mocked on the decision board (`--s-c-rest-text`): the teal's words are
    // lighter than the teal. On the teal's own 9.4% tint over a card the teal is 4.4:1.
    restWords: '#6CCACA', // 6.7:1 on the teal's tint; 9.6:1 on the room
    restSky: CC.accentSky,
    restDay: CC.restDay,
    dayMissed: 'rgba(168,209,191,0.38)', // PROVISIONAL, as mocked on the decision board (`--s-dot-missed`): the mist at 38%
    // D13, PROVISIONAL. A session's colour is also the WORDS of its pill, on its own 12.5% tint
    // over a card: the tightest place it is used, and the number each is held to (4.5:1). The
    // workout card's dark test measures it there.
    cardio: '#EE8A3C', // 4.9:1 on its tint; 7.3:1 on the room. The orange itself (#E87722) is 4.25:1 on its tint
    cardioHard: '#E58BB2', // as mocked on the decision board (`--s-c-hard`): 5.0:1 on its tint; 7.6:1 on the room. The mauve itself is 3.3:1 on the room
    cardioEasy: CC.thrive, // 6.1:1 on its tint; 9.7:1 on the room. Cedar Prime is 2.9:1 on the room. The accent ink's own dark value
    strength: '#B794F4', // 4.9:1 on its tint; 7.5:1 on the room. The purple itself is 2.6:1 on the room
    mobility: '#6FB096', // 4.8:1 on its tint; 7.3:1 on the room. Cedar Light itself (#5FA287) is 4.12:1 on its tint
    // PROVISIONAL. A kind of routine is the words of a pill too, on its own 9.4% tint over a
    // card. The mobility card's dark test measures each there.
    routine: {
      yoga: '#A78BFA', // 4.7:1 on its tint; 6.8:1 on the room. The purple itself (#8B5CF6) is 3.2:1 on its tint
      rehab: '#F87171', // 4.7:1 on its tint: the dark error red (D12). #EF4444 is 3.6:1 on its tint
      smr: '#F472B6', // 4.9:1 on its tint; 7.0:1 on the room. The pink itself (#EC4899) is 3.9:1 on its tint
      joint: CC.success, // 5.0:1 on its tint: kept
    },
    // D13, PROVISIONAL. A band is a dot, a mark: held to 3:1 on a drawer's body, on a card,
    // on a card's recessed panel and on the instrument's glass. Four of the six read as
    // they are and are kept. The mock draws a week of them (thrive, Cedar Light, amber) and
    // no peak and no depleted day.
    readiness: {
      // Cedar Prime, the peak, is 2.4:1 on a card and 1.9:1 on the glass. Its twin is the
      // brand's deeper Thrive: still the deepest of the three greens.
      peak: CC.thriveDeep, // 5.3:1 on a card; 4.4:1 on the glass
      fresh: CC.cedarLight, // 5.0:1; 4.1:1
      neutral: CC.thrive, // 7.9:1; 6.5:1
      tired: CC.warning, // 7.0:1; 5.7:1
      // The dark theme's red (D12). #EF4444 is 3.99:1 on a card and 3.27:1 on the glass: it
      // would pass as a mark, and there would then be two reds in one theme.
      depleted: '#F87171', // 5.4:1; 4.5:1
      unknown: CC.textLighter, // 5.9:1; 4.9:1
    },
    readinessOnPale,
    sleepStage,
    // PROVISIONAL, and not on the mock. The same ramp, turned over. Roots, the deepest sleep
    // in light, is 1.28:1 on a dark card; so in dark the deepest sleep is the LIGHTEST green,
    // as the race day is the lightest mark on a dark chart. A length written on a stage takes
    // whichever ink reads on it.
    sleepChart: {
      deep: { fill: CC.cedarMist, ink: CC.cedarInk }, // 8.93:1 on the chart's card; its length 11.01:1
      rem: { fill: CC.thriveDeep, ink: CC.cedarInk }, // 5.32:1; 6.57:1 (white is 2.81:1)
      light: { fill: CC.cedarPrime, ink: CC.surface }, // 2.36:1, where light's Cedar Mist is 1.51:1; 6.33:1
      awake: { fill: CC.warning, ink: CC.cedarInk }, // 6.96:1; 8.58:1 (white is 2.15:1)
      unstaged: { fill: DARK_FROST, ink: GLASS_TEXT }, // a lift on the card, 1.28:1, where light's cannot be seen
    },
    // PROVISIONAL, and not on the mock. Light's order — the stronger, the deeper the green —
    // cannot be read on a dark card, where Cedar Prime is the faintest of the three. So it is
    // turned over, as the sleep chart's is: the stronger, the brighter. Each chip is the same
    // tint of its rail, under the glass ink.
    evidence: {
      strong: { rail: CC.thrive, chipBg: 'rgba(59,212,155,0.16)', chipInk: GLASS_TEXT },
      moderate: { rail: CC.thriveDeep, chipBg: 'rgba(43,174,130,0.14)', chipInk: GLASS_TEXT },
      emerging: { rail: CC.cedarLight, chipBg: 'rgba(95,162,135,0.18)', chipInk: GLASS_TEXT },
    },
    roadmapBadge: {
      under_review: { bg: 'rgba(255,255,255,0.12)', fg: GLASS_TEXT },
      planned: { bg: 'rgba(168,209,191,0.16)', fg: CC.thrive },
      in_progress: { bg: 'rgba(59,212,155,0.18)', fg: CC.thrive },
      shipped: { bg: 'rgba(59,212,155,0.14)', fg: CC.thrive },
      feature: { bg: 'rgba(59,212,155,0.10)', fg: CC.thrive },
      improvement: { bg: 'rgba(59,212,155,0.12)', fg: CC.thrive },
      known_issue: { bg: 'rgba(245,158,11,0.16)', fg: CC.warning },
      neutral: { bg: 'rgba(255,255,255,0.12)', fg: GLASS_TEXT },
    },
    // PROVISIONAL, after the zones drawer's chips: the status colour on a tint of itself. The
    // neutral chip is thrive under Cedar ink in both, as a pale pill is.
    tierBadge: {
      success: { bg: 'rgba(16,185,129,0.16)', fg: CC.success },
      warning: { bg: 'rgba(245,158,11,0.16)', fg: CC.warning },
      info: { bg: 'rgba(96,165,250,0.14)', fg: '#60A5FA' },
      neutral: { bg: CC.thrive, fg: CC.cedarInk },
    },
    // PROVISIONAL, and not on the mock. Four steps over the room, strongest first: Cedar Light at
    // 24% (1.46:1 off the room), Cedar Mist at 16% (1.41:1), the subtle surface (1.25:1) and a
    // card's lift (1.23:1). Words are the glass ink and a row's name the accent; the quieter
    // line is the second ink. The practice card inside the first is the subtle surface, and its
    // second ink is 4.72:1 there (light's grey on its Cedar Subtle is 4.23:1).
    explainerTone: {
      cedarLight: { bg: 'rgba(95,162,135,0.24)', titleColor: GLASS_TEXT, iconColor: GLASS_TEXT, bodyTextColor: GLASS_TEXT, subtleTextColor: GLASS_TEXT_SECONDARY, emphasisTextColor: GLASS_TEXT, dividerColor: 'rgba(255,255,255,0.12)' },
      cedarMist: { bg: 'rgba(168,209,191,0.16)', titleColor: CC.thrive, iconColor: CC.thrive, bodyTextColor: GLASS_TEXT, subtleTextColor: GLASS_TEXT_SECONDARY, emphasisTextColor: CC.thrive, dividerColor: 'rgba(255,255,255,0.12)' },
      cedarSubtle: { bg: 'rgba(59,212,155,0.12)', titleColor: GLASS_TEXT, iconColor: CC.thrive, bodyTextColor: GLASS_TEXT, subtleTextColor: GLASS_TEXT_SECONDARY, emphasisTextColor: CC.thrive, dividerColor: 'rgba(255,255,255,0.12)' },
      limestone: { bg: DARK_FROST, titleColor: GLASS_TEXT, iconColor: CC.thrive, bodyTextColor: GLASS_TEXT, subtleTextColor: GLASS_TEXT_SECONDARY, emphasisTextColor: CC.thrive, dividerColor: 'rgba(255,255,255,0.12)' },
    },
    reportChart,
    zoneRamp,
    zoneWords: { five: zoneWordsInDark(zoneWords.five), polarized_3: zoneWordsInDark(zoneRamp.polarized_3), friel_7: zoneWordsInDark(zoneRamp.friel_7) },
    zoneBarLabel: { polarized_3: zoneBarLabelInDark(zoneRamp.polarized_3), friel_7: zoneBarLabelInDark(zoneRamp.friel_7) },
    powerRamp,
    effortRamp,
    // D13, PROVISIONAL. An event is a thin line or a small dot on a chart's card.
    annotation: {
      milestone: CC.thriveDeep, // 5.3:1 on a card. Cedar Prime is 2.4:1
      planChange: CC.heat, // 3.9:1: kept
      healthSignal: CC.warning, // 7.0:1: kept
      // Roots, the day itself, is the darkest mark on a chart in light and 1.3:1 on a dark
      // card. As on the week strip, in dark the race is the lightest mark instead.
      raceDay: GLASS_TEXT, // 12.9:1
      unknown: CC.textLighter, // 5.9:1: kept
    },
    // PROVISIONAL. A kind of phase keeps its colour: the bar and the icon say what the phase IS.
    // The one that is the brand green is the accent here, since Cedar Prime is 2.4:1 on a dark card.
    // The main lift's violet is 4.17:1 against a light card and 3.55:1 against a dark one: it is
    // lifted until it is no fainter (4.28:1), and is still not the accessory's lilac.
    phaseBar: { ...phaseBar, main: '#9770F7', other: CC.thrive },
    strengthBlock: { ...strengthBlock, main: CC.thrive },
    // PROVISIONAL. The second power bar's purple is 3.89:1 on a light card and 3.78:1 on a dark one:
    // #AC5EF7 is 4.06:1.
    targetBar: { ...targetBar, power: [targetBar.power[0], '#AC5EF7', targetBar.power[2], targetBar.power[3], targetBar.power[4]] },
    // PROVISIONAL. Three power zones are fainter on a dark card than on a light one: the second's violet
    // (4.16:1 in light, 3.53:1 in dark), the sixth's red (4.75:1, 3.10:1) and the seventh's dark red
    // (8.17:1, 1.80:1). Each is lifted until it is not: 4.33:1, 5.40:1 and 8.49:1.
    timeInZone: { ...timeInZone, power: [timeInZone.power[0], '#9B72F2', timeInZone.power[2], timeInZone.power[3], timeInZone.power[4], '#F87171', '#FDAFAF'] },
    // PROVISIONAL. In dark the chart and its tiles are cut into the card, and every line is stronger there
    // than it is in light. One is not as WORDS on its own pill, which is a tint of it on the card: the violet
    // (3.58:1 in light, 3.11:1 in dark). It is lifted to the zones card's violet: 3.68:1.
    timelineLine: { ...timelineLine, cadence: '#9B72F2', grade: '#9B72F2', vert_ratio: '#9B72F2' },
    // PROVISIONAL. The fifth zone's tint is the strongest of the five on a light chart (1.27:1) and was
    // fainter on a dark one (1.20:1). A quarter of the zone's red where light has a little under a fifth: 1.31:1.
    zoneBand: [zoneBand[0], zoneBand[1], zoneBand[2], zoneBand[3], withAlpha(hrZone.zone5, 0.25)],
    // PROVISIONAL. Three of the drawer's tints are fainter on a dark chart than on a light one: the three-zone
    // red (1.27:1 in light, 1.20:1 in dark), the seven-zone red (1.17:1, 1.12:1) and deep red (1.38:1, 1.09:1).
    // Each is lifted until it is not: 1.31:1, 1.20:1 and 1.47:1.
    ownZoneBand: {
      polarized_3: [ownZoneBand.polarized_3[0], ownZoneBand.polarized_3[1], withAlpha(zoneRamp.polarized_3[2], 0.25)],
      friel_7: [...ownZoneBand.friel_7.slice(0, 5), withAlpha(zoneRamp.friel_7[5], 0.188), withAlpha(zoneRamp.friel_7[6], 0.5)],
    },
    // PROVISIONAL. The two hardest power zones are fainter in a dark track than in a light one: the burnt
    // orange (4.50:1 in light, 2.89:1 in dark) and the dark red (8.70:1, 1.49:1). Each is lifted until it
    // is not, on its tile and in its track.
    zoneStrip: { ...zoneStrip, power: [...timeInZone.heartRate, '#F87171', '#FECACA'] },
    // PROVISIONAL: each hue at 16% under a lighter ink of it, inside the hue at 40%; the grey is white the same way.
    metricPill: {
      purple: { bg: 'rgba(168,85,247,0.16)', text: '#D8B4FE', border: 'rgba(168,85,247,0.40)' },
      orange: { bg: 'rgba(249,115,22,0.16)', text: '#FDBA74', border: 'rgba(249,115,22,0.40)' },
      teal: { bg: 'rgba(20,184,166,0.16)', text: '#5EEAD4', border: 'rgba(20,184,166,0.40)' },
      blue: { bg: 'rgba(59,130,246,0.16)', text: '#93C5FD', border: 'rgba(59,130,246,0.40)' },
      red: { bg: 'rgba(239,68,68,0.16)', text: '#FCA5A5', border: 'rgba(239,68,68,0.40)' },
      green: { bg: 'rgba(34,197,94,0.16)', text: '#86EFAC', border: 'rgba(34,197,94,0.40)' },
      gray: { bg: 'rgba(255,255,255,0.08)', text: GLASS_TEXT, border: 'rgba(255,255,255,0.16)' },
    },
    // PROVISIONAL. A badge keeps its fill under Cedar ink, all but two. The violet: Cedar ink on it is
    // 4.35:1 (white, as in light, 4.23:1), and on the accessory lilac it is 6.77:1. The blue: it is
    // fainter on a dark box (3.18:1) than on a light one (3.49:1), and the theme's info blue is not.
    segmentRank: { ...segmentRank, kom: '#A78BFA', top10: '#60A5FA' },
    // PROVISIONAL. As in light: each of the three is a mark that reads on a dark card, and the
    // green and the amber are words there too (palette.contrast.test.ts has the numbers).
    effortBand,
    // Not provisional: the card they are drawn on is the same glass in both themes (D5).
    adherenceBar,
    // PROVISIONAL. The card kept a dark look of its own that no phone was handed, and the 7-day
    // line is that look's. Its budget lines were white at 35%, fainter on the chart than light's
    // (2.87:1 against 3.30:1): 42% is the first step that is not (3.48:1). Today's bar is the
    // accent, so that it stands out of the week as deep green does in light; a day with nothing
    // and the placeholder are white where light has black.
    loadChart: { ...loadChart, today: CC.thrive, empty: 'rgba(255,255,255,0.08)', pending: 'rgba(255,255,255,0.18)', budget: 'rgba(255,255,255,0.42)', sevenDay: '#E8A56B' },
    weather,
    // PROVISIONAL. The dark twins of the session colours they are in light (data.cardioEasy,
    // cardio, cardioHard, strength; palette.test.ts holds them together): Cedar Prime is 1.9:1 on
    // a dark card, the mauve 2.9:1 and the purple 2.3:1.
    discipline: { swimming: CC.thrive, cycling: '#EE8A3C', running: '#E58BB2', station: '#B794F4' },
    // Not provisional: a fill under a white glyph keeps its colour, as the brand fill does.
    disciplineTile,
    partner,
  },
  chat: {
    // D14, PROVISIONAL (the mockups' "no border"): Cedar's bubble is a frost card in dark, and
    // a card's edge is this hairline. The other option keeps the green outline.
    bubbleEdge: DARK_EDGE,
    // The dark twin of `cedarPrimeAtRest`: mint at the dots' resting 30%. Faint by design, as
    // in light (1.6:1 there): the pulse that sweeps through it is the full accent.
    accentAtRest: 'rgba(59,212,155,0.30)',
  },
  overlay: {
    // D9, PROVISIONAL (recommended): the light scrim is Cedar ink at 70%, which over a
    // Cedar ink room changes nothing (1.00:1).
    scrim: 'rgba(0,0,0,0.55)',
    backdrop: '#000000',
  },
  timerNote,
  share,
  shareCard,
  campaign,
  animation,
  wall,
  legal,
  notFound,
  timer: TIMER_DARK,
  island,
  control: {
    // PROVISIONAL, as mocked on the decision board's drawer (`--m-switch-on`, `--m-switch-off`):
    // thrive when on, white at 18% when off, under a white thumb. The white thumb on thrive is
    // 1.90:1: which side the thumb is on says which way the switch is, as it does in light on
    // the OFF track (1.24:1). Each theme has one track the thumb is faint on (palette.test.ts).
    switchOn: CC.thrive,
    switchOff: 'rgba(255,255,255,0.18)',
    switchThumb: CC.surface,
  },
  glass,
  system: {
    statusBarStyle: 'light-content',
    navigationBarStyle: 'light',
    keyboardAppearance: 'dark',
    pickerVariant: 'dark',
    blurTint: 'dark',
  },
};

export const palettes: Readonly<Record<Scheme, Palette>> = { light, dark };

/**
 * White at `alpha`, for inks and edges on ALWAYS-DARK glass (a Roots header, a hero
 * card) and nowhere else. It does not theme: a white-alpha ink on a surface that is
 * light in light mode is a bug in both.
 */
export function onGlass(alpha: number): string {
  if (!(alpha >= 0 && alpha <= 1)) {
    throw new Error(`theme/palette: onGlass needs an alpha in 0..1, got ${alpha}`);
  }
  return `rgba(255,255,255,${alpha})`;
}
